import { getClientId, ObjectWithClientId } from "client/Misc/clientId";
import {
	ReactElement,
	useCallback,
	useMemo
} from "react";
import {
	CommunityPostImageFormData,
	CommunityPostImageFormModelKind,
	getCommunityPostImageUrl,
	PendingCommunityPostImageFormData
} from "./common";
import {
	generateTrackingId,
	isNil,
	setTrackingId
} from "client/Misc/utils";
import { FormImageAttachment } from "../../FormImageAttachment";
import { IValueInputAttachable } from "client/Forms/attachable";
import { CommunityPostImageViewModel } from "client/Api/Communities/communityPostViewer";
import { fromArray } from "client_legacy/ndaw/mapHelpers";
import { never } from "client_legacy/ndaw/errors";
import styles from "./CommunityPostComposerImages.module.less";
import classNames from "classnames";

export type CommunityPostComposerImagesProps = {
	attachable: IValueInputAttachable<readonly ObjectWithClientId<CommunityPostImageFormData>[]>;
	initialImages: readonly CommunityPostImageViewModel[];
	areImagesScrollable?: boolean;
};

export const CommunityPostComposerImages = (props: CommunityPostComposerImagesProps): ReactElement => {
	const {
		attachable,
		initialImages,
		areImagesScrollable = false
	} = props;

	const images: readonly ObjectWithClientId<CommunityPostImageFormData>[] = attachable.value;
	const existingImagesById: ReadonlyMap<number, CommunityPostImageViewModel> = useMemo(
		() => fromArray(initialImages, x => x.id),
		[initialImages]
	);

	const deleteImageHandler = useCallback(
		(index: number): void => {
			const image: ObjectWithClientId<CommunityPostImageFormData> = attachable.reducer
				.value[index];

			if (image.kind === CommunityPostImageFormModelKind.Loading) {
				image.cancel();
			}

			attachable
				.reducer
				.removeAt(index);

			attachable
				.validation
				.triggerValueChanged();
		},
		[attachable]
	);

	return (
		<ul
			{...setTrackingId("images")}
			className={classNames(
				styles.root,
				{
					[styles.root_scroll]: areImagesScrollable
				}
			)}
		>
			{images.map(
				(x, index): ReactElement => (
					<li key={getClientId(x)}>
						<ImageItem
							image={x}
							existingImagesById={existingImagesById}
							onDelete={() => deleteImageHandler(index)}
							trackingId={generateTrackingId("attachment", `|${getClientId(x)}`)}
						/>
					</li>
				)
			)}
		</ul>
	);
};
CommunityPostComposerImages.displayName = "CommunityPostComposerImages";

type ImageItemProps = {
	image: ObjectWithClientId<CommunityPostImageFormData>;
	existingImagesById: ReadonlyMap<number, CommunityPostImageViewModel>;
	onDelete: () => void;
	trackingId: string;
};

const ImageItem = (props: ImageItemProps): ReactElement => {
	const {
		image,
		onDelete,
		trackingId,
		existingImagesById,
	} = props;

	switch (image.kind) {
		case CommunityPostImageFormModelKind.Loading:
			return (
				<FormImageAttachment
					isLoading
					onDelete={onDelete}
					hasBackground
					trackingId={trackingId}
				/>
			);
		case CommunityPostImageFormModelKind.New:
		case CommunityPostImageFormModelKind.Existing:
			return (
				<FormImageAttachment
					src={getImageUrl(image, existingImagesById)}
					onDelete={onDelete}
					hasBackground
					trackingId={trackingId}
				/>
			);
		default:
			return never(image);
	}
};
ImageItem.displayName = "ImageItem";

function getImageUrl(
	model: ObjectWithClientId<Exclude<CommunityPostImageFormData, PendingCommunityPostImageFormData>>,
	existingImagesById: ReadonlyMap<number, CommunityPostImageViewModel>
): string {
	switch (model.kind){
		case CommunityPostImageFormModelKind.New:
			return model.image
				.uri;
		case CommunityPostImageFormModelKind.Existing:
			const existingImage: CommunityPostImageViewModel | undefined = existingImagesById
				.get(model.id);

			if (isNil(existingImage)) {
				throw new Error(`Could not find image with id '${model.id}'`);
			}

			return getCommunityPostImageUrl(existingImage.blobId);
		default:
			never(model);
	}
}